import React, { useState } from 'react'
import Head from 'next/head'
import Header from '../header'
import Footer from '../footer'
import styles from "../../styles/BmcHelix.module.css"
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { IMAGE_DELIVERY } from '@/base/utils/dataHelper';
import StoneityButton from '@/components/stoneity-button/stoneityButton';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from "swiper";
import "swiper/css";

enum ItsmUseCasesEnum {
  Service,
  Problem,
  Communication,
  Operations
}

type BmcHelixItsmProps = InferGetStaticPropsType<typeof getStaticProps> & {
  setWidgetMenuOpen: (value: boolean) => void;
};

const BmcHelixItsm = ({ setWidgetMenuOpen }: BmcHelixItsmProps) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const [productFeaturesTab, setProductFeaturesTab] = useState(ItsmUseCasesEnum.Service);

  const baseUrl = 'https://stoneity.com';
  const currentPath = router.asPath;
  const locale = router.locale || 'en';
  const canonicalUrl = locale === 'tr'
    ? `${baseUrl}/tr${currentPath}`
    : `${baseUrl}${currentPath}`;
  const alternateLocale = locale === 'tr' ? 'en' : 'tr';
  const alternatePath = '/bmc-helix-itsm';

  return (
    <>
      <Head>
        <title>{t('seo_bmc_helix_itsm_title')}</title>
        <meta name="description" content={t('seo_bmc_helix_itsm_description')} />
        <meta name="keywords" content="BMC Helix ITSM, ITSM, servis yönetimi, service management, AI agents, incident management, Stoneity" />
        <link key="canonical" rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={t('seo_bmc_helix_itsm_title')} />
        <meta property="og:description" content={t('seo_bmc_helix_itsm_description')} />
        <meta property="og:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta property="og:site_name" content="Stoneity" />
        <meta property="og:locale" content={locale === 'tr' ? 'tr_TR' : 'en_US'} />
        <meta property="og:locale:alternate" content={locale === 'tr' ? 'en_US' : 'tr_TR'} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={t('seo_bmc_helix_itsm_title')} />
        <meta name="twitter:description" content={t('seo_bmc_helix_itsm_description')} />
        <meta name="twitter:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta name="twitter:site" content="@stoneity" />

        <link rel="alternate" hrefLang={locale} href={canonicalUrl} />
        <link rel="alternate" hrefLang={alternateLocale} href={`${baseUrl}/${alternateLocale}${alternatePath}`} />
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${alternatePath}`} />
      </Head>
      <Header />
      <main className={styles.mainFlex}>
        <div className={styles.pageFlex}>
          <div className={styles.discoveryBannerWrapper}>
            <div className={styles.discoveryBannerLeft}>
              <div className={styles.discoveryBannerTitle}>BMC Helix ITSM</div>
              <div className={styles.discoveryBannerExplain}>{t("integratedAiDrivenServiceManagement")}</div>
              <div className={styles.talkWithUsButtonWrapper}>
                <div className={styles.talkWithUsButtonImgWrapper}>
                  <img src="/human-1.png" alt="" />
                  <img src="/human-2.png" alt="" />
                  <img src="/human-3.png" alt="" />
                </div>
                <StoneityButton
                  buttonClass="talkWithUsButton"
                  buttonText={t("talkWithUs")}
                  buttonStyle="light"
                  width={200}
                  height={45}
                  onClick={() => setWidgetMenuOpen(true)}
                />
              </div>
            </div>
            <Image
              className={styles.discoveryBannerImg}
              src={`${IMAGE_DELIVERY}/8e91c020-382f-4967-2309-308140ddde00/690x500`}
              alt=""
              width={690}
              height={500}
              priority
              sizes="(max-width: 1100px) 350px, (max-width: 1250px) 450px, 600px"
            />
            {/* <img className={styles.discoveryBannerImg} src="/helixDiscoveryBanner.svg" alt="" /> */}
          </div>
        </div>
        <div className={styles.pageFlex}>
          <div className={styles.discoveryOverviewWrapper}>
            <div className={styles.discoveryOverviewTop}>
              <div className={styles.discoveryOverviewTitle}>{t("boostYourItsm")}</div>
              {/* <div className={styles.discoveryOverviewExplain}>Available for deployment on SaaS or on-premises</div> */}
            </div>
            <div className={styles.discoveryOverviewBottom}>
              <div className={styles.discoveryOverviewBottomRow}>
                <div className={styles.discoveryOverviewBottomRowTitle}>90%</div>
                <div className={styles.discoveryOverviewBottomRowExplain}>{t("successfulRootCauseIdentification")}</div>
              </div>
              <div className={styles.discoveryOverviewBottomRow}>
                <div className={styles.discoveryOverviewBottomRowTitle}>60%</div>
                <div className={styles.discoveryOverviewBottomRowExplain}>{t("fasterIncidentResolution")}</div>
              </div>
              <div className={styles.discoveryOverviewBottomRow}>
                <div className={styles.discoveryOverviewBottomRowTitle}>75%</div>
                <div className={styles.discoveryOverviewBottomRowExplain}>{t("increaseOperationalEfficiency")}</div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.pageFlex}>
          <div className={styles.discoveryExplainRowWrapper}>
            <div className={styles.discoveryExplainRow}>
              <div className={styles.discoveryExplainRowLeft}>
                <div className={styles.discoveryExplainRowTitle}>{t("betterSupportOutcomes")}</div>
                {/* <div className={styles.discoveryExplainRowExplain}>Equip IT and stakeholders with a dynamic view of all IT assets, services, and relationships so they can improve operations and minimize risk.</div> */}
                <ul className={styles.discorveryExplainRowItem}>
                  <li>
                    {t("aiAgents")}
                    <span className={styles.discorveryExplainRowItemDescription}>{t("aiAgentsDescription")}</span>
                  </li>
                  <li>
                    {t("integratedKnowledgeManagement")}
                    <span className={styles.discorveryExplainRowItemDescription}>{t("integratedKnowledgeManagementDescription")}</span>
                  </li>
                  <li>
                    {t("conversationalCustomerAssistance")}
                    <span className={styles.discorveryExplainRowItemDescription}>{t("conversationalCustomerAssistanceDescription")}</span>
                  </li>
                </ul>
              </div>
              <img className={styles.discoveryExplainRowImg} src="/kb-realize-service-operations-excellence.svg" alt="" />
            </div>
            <div className={styles.discoveryExplainRow}>
              <img className={styles.discoveryExplainRowImg} src="/kb-move-reactive-proactive.svg" alt="" />
              <div className={styles.discoveryExplainRowLeft}>
                <div className={styles.discoveryExplainRowTitle}>{t("respondFasterToIssues")}</div>
                {/* <div className={styles.discoveryExplainRowExplain}>Ingest BMC Helix Discovery topology data and third-party data into the BMC Helix platform to create an exceptionally rich source of truth, powering observability and delivering AIOps insights.</div> */}
                <ul className={styles.discorveryExplainRowItem}>
                  <li>
                    {t("aiDrivenIncidentClustering")}
                    <span className={styles.discorveryExplainRowItemDescription}>{t("aiDrivenIncidentClusteringDescription")}</span>
                  </li>
                  <li>
                    {t("betterCollaborationWithAi")}
                    <span className={styles.discorveryExplainRowItemDescription}>{t("betterCollaborationWithAiDescription")}</span>
                  </li>
                  <li>
                    {t("intelligentChangeRiskAnalysis")}
                    <span className={styles.discorveryExplainRowItemDescription}>{t("intelligentChangeRiskAnalysisDescription")}</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className={styles.discoveryExplainRow}>
              <div className={styles.discoveryExplainRowLeft}>
                <div className={styles.discoveryExplainRowTitle}>{t("serviceAndOperationsExcellence")}</div>
                {/* <div className={styles.discoveryExplainRowExplain}>Use detailed asset data to pinpoint risks, locate backdoor entry points, and strengthen regulatory compliance.</div> */}
                <ul className={styles.discorveryExplainRowItem}>
                  <li>
                    {t("integratedDiscoveryAndAiops")}
                    <span className={styles.discorveryExplainRowItemDescription}>{t("integratedDiscoveryAndAiopsDescription")}</span>
                  </li>
                  <li>
                    {t("unifiedServiceAndOperationsManagement")}
                    <span className={styles.discorveryExplainRowItemDescription}>{t("unifiedServiceAndOperationsManagementDescription")}</span>
                  </li>
                  <li>
                    {t("knowledgeCaptureAndLearning")}
                    <span className={styles.discorveryExplainRowItemDescription}>{t("knowledgeCaptureAndLearningDescription")}</span>
                  </li>
                </ul>
              </div>
              <img className={styles.discoveryExplainRowImg} src="/agentic-ai-bmc-helixgpt.svg" alt="" />
            </div>
          </div>
        </div>
        <div className={styles.pageFlex}>
          <div className={styles.discoveryUseCasesWrapper}>
            <div className={styles.discoveryUseCasesTitle}>{t("discoverHelixItsmUseCases")}</div>
            <div className={styles.discoveryUseCasesTabWrapper}>
              <div className={`${styles.discoveryUseCasesTab} ${productFeaturesTab === ItsmUseCasesEnum.Service ? styles.discoveryUseCasesTabActive : ''}`} onClick={() => { setProductFeaturesTab(ItsmUseCasesEnum.Service) }}>{t("serviceDeskExcellence")}</div>
              <div className={`${styles.discoveryUseCasesTab} ${productFeaturesTab === ItsmUseCasesEnum.Problem ? styles.discoveryUseCasesTabActive : ''}`} onClick={() => { setProductFeaturesTab(ItsmUseCasesEnum.Problem) }}>{t("proactiveProblemResolution")}</div>
              <div className={`${styles.discoveryUseCasesTab} ${productFeaturesTab === ItsmUseCasesEnum.Communication ? styles.discoveryUseCasesTabActive : ''}`} onClick={() => { setProductFeaturesTab(ItsmUseCasesEnum.Communication) }}>{t("collaborationAndCommunication")}</div>
              <div className={`${styles.discoveryUseCasesTab} ${productFeaturesTab === ItsmUseCasesEnum.Operations ? styles.discoveryUseCasesTabActive : ''}`} onClick={() => { setProductFeaturesTab(ItsmUseCasesEnum.Operations) }}>{t("unifiedServiceOperationsManagement")}</div>
            </div>
            {productFeaturesTab === ItsmUseCasesEnum.Service &&
              <div className={styles.discoveryUseCasesContent}>
                <div className={styles.discoveryUseCasesContentLeft}>
                  <div className={styles.discoveryUseCasesContentLeftTitle}>{t("serviceDeskExcellence")}</div>
                  <div className={styles.discoveryUseCasesContentLeftExplain}>{t("serviceDeskExcellenceDescription")}</div>
                </div>
                <img className={styles.discoveryUseCasesContentImg} src="/ill-use-cases-service-desk-excellence.svg" alt="" />
              </div>
            }
            {productFeaturesTab === ItsmUseCasesEnum.Problem &&
              <div className={styles.discoveryUseCasesContent}>
                <div className={styles.discoveryUseCasesContentLeft}>
                  <div className={styles.discoveryUseCasesContentLeftTitle}>{t("proactiveProblemResolution")}</div>
                  <div className={styles.discoveryUseCasesContentLeftExplain}>{t("proactiveProblemResolutionDescription")}</div>
                </div>
                <img className={styles.discoveryUseCasesContentImg} src="/ill-use-cases-proactive-problem-resolution.svg" alt="" />
              </div>
            }
            {productFeaturesTab === ItsmUseCasesEnum.Communication &&
              <div className={styles.discoveryUseCasesContent}>
                <div className={styles.discoveryUseCasesContentLeft}>
                  <div className={styles.discoveryUseCasesContentLeftTitle}>{t("collaborationAndCommunication")}</div>
                  <div className={styles.discoveryUseCasesContentLeftExplain}>{t("collaborationAndCommunicationDescription")}</div>
                </div>
                <img className={styles.discoveryUseCasesContentImg} src="/ill-use-cases-collaboration-and-communication.svg" alt="" />
              </div>
            }
            {productFeaturesTab === ItsmUseCasesEnum.Operations &&
              <div className={styles.discoveryUseCasesContent}>
                <div className={styles.discoveryUseCasesContentLeft}>
                  <div className={styles.discoveryUseCasesContentLeftTitle}>{t("unifiedServiceOperationsManagement")}</div>
                  <div className={styles.discoveryUseCasesContentLeftExplain}>{t("unifiedServiceOperationsManagementDescription")}</div>
                </div>
                <img className={styles.discoveryUseCasesContentImg} src="/ill-use-cases-unified-service-and-operations-management.svg" alt="" />
              </div>
            }
          </div>
          <div className={styles.discoveryUseCasesResponsiveWrapper}>
            <Swiper
              slidesPerView={1}
              className={styles.discoveryUseCasesResponsiveMySwiper}
              modules={[Navigation]}
              navigation={{
                prevEl: "." + styles.discoveryUseCasesResponsiveSlideWrapper,
                nextEl: "." + styles.discoveryUseCasesResponsiveSlideWrapper2,
              }}
            >
              <SwiperSlide>
                <div className={styles.discoveryUseCasesContentResponsive}>
                  <div className={styles.discoveryUseCasesContentLeft}>
                    <div className={styles.discoveryUseCasesContentLeftTitle}>{t("serviceDeskExcellence")}</div>
                    <div className={styles.discoveryUseCasesContentLeftExplain}>{t("serviceDeskExcellenceDescription")}</div>
                  </div>
                  <img className={styles.discoveryUseCasesContentImg} src="/ill-use-cases-service-desk-excellence.svg" alt="" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className={styles.discoveryUseCasesContentResponsive}>
                  <div className={styles.discoveryUseCasesContentLeft}>
                    <div className={styles.discoveryUseCasesContentLeftTitle}>{t("proactiveProblemResolution")}</div>
                    <div className={styles.discoveryUseCasesContentLeftExplain}>{t("proactiveProblemResolutionDescription")}</div>
                  </div>
                  <img className={styles.discoveryUseCasesContentImg} src="/ill-use-cases-proactive-problem-resolution.svg" alt="" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className={styles.discoveryUseCasesContentResponsive}>
                  <div className={styles.discoveryUseCasesContentLeft}>
                    <div className={styles.discoveryUseCasesContentLeftTitle}>{t("collaborationAndCommunication")}</div>
                    <div className={styles.discoveryUseCasesContentLeftExplain}>{t("collaborationAndCommunicationDescription")}</div>
                  </div>
                  <img className={styles.discoveryUseCasesContentImg} src="/ill-use-cases-collaboration-and-communication.svg" alt="" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className={styles.discoveryUseCasesContentResponsive}>
                  <div className={styles.discoveryUseCasesContentLeft}>
                    <div className={styles.discoveryUseCasesContentLeftTitle}>{t("unifiedServiceOperationsManagement")}</div>
                    <div className={styles.discoveryUseCasesContentLeftExplain}>{t("unifiedServiceOperationsManagementDescription")}</div>
                  </div>
                  <img className={styles.discoveryUseCasesContentImg} src="/ill-use-cases-unified-service-and-operations-management.svg" alt="" />
                </div>
              </SwiperSlide>
            </Swiper>
            <div className={styles.discoveryUseCasesResponsiveSlideWrapper}>
              <div className={styles.discoveryUseCasesResponsiveCustomLeftArrow}></div>
            </div>
            <div className={styles.discoveryUseCasesResponsiveSlideWrapper2}>
              <div className={styles.discoveryUseCasesResponsiveCustomRightArrow}></div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale || "tr", ["common"])),
  },
});

export default BmcHelixItsm;
